import rankFuzzy, { normalizeForMatch, singularize, splitWords } from "~/helpers/search/fuzzyMatch";

const STOP_WORDS = new Set([
	"a",
	"an",
	"the",
	"to",
	"me",
	"my",
	"go",
	"goto",
	"take",
	"show",
	"open",
	"view",
	"page",
	"screen",
	"please",
	"navigate",
	"of",
	"for",
	"on",
	"in",
	"at"
]);

const SYNONYMS = new Map<string, string>([
	["catalog", "items"],
	["customer", "customers"],
	["inventory", "items"],
	["order", "orders"],
	["product", "items"],
	["products", "items"],
	["recipe", "recipes"]
]);

const SYNONYM_SCORE_PENALTY = 10;

const normalize = (value: string): string => value.trim().toLowerCase();

const splitTypedWords = (value: string): string[] => splitWords(value).filter((word) => !STOP_WORDS.has(word));

const queryWords = (value: string): string[] => Array.from(new Set(splitTypedWords(value)));

const findSynonym = (word: string): string | undefined => SYNONYMS.get(word) ?? SYNONYMS.get(singularize(word));

export interface LabelMatch<TItem> {
	item: TItem;
	score: number;
}

export interface RankByLabelOptions {
	limit?: number;
	minLabelLength?: number;
	shouldRequireAllWords?: boolean;
}

const rankByLabel = <TItem>(
	items: TItem[],
	getLabel: (item: TItem) => string,
	query: string,
	options: RankByLabelOptions = {}
): LabelMatch<TItem>[] => {
	const { limit, minLabelLength = 0, shouldRequireAllWords = true } = options;
	const typedWords = queryWords(query);
	if (typedWords.length === 0) return [];
	const candidates = items.filter((item) => normalize(getLabel(item)).length >= minLabelLength);
	const directMatches = rankFuzzy(candidates, getLabel, typedWords, { shouldRequireAllWords });
	const synonymWords = typedWords.map((word) => findSynonym(word) ?? word);
	const directlyMatchedItems = new Set(directMatches.map((match) => match.item));
	const synonymMatches = synonymWords.some((word, index) => word !== typedWords[index])
		? rankFuzzy(
				candidates.filter((item) => !directlyMatchedItems.has(item)),
				getLabel,
				synonymWords,
				{ shouldRequireAllWords }
			)
		: [];
	const rankedMatches = [
		...directMatches.map(({ item, score }) => ({ item, score })),
		...synonymMatches.map(({ item, score }) => ({ item, score: score - SYNONYM_SCORE_PENALTY }))
	].sort((a, b) => b.score - a.score);
	return limit === undefined ? rankedMatches : rankedMatches.slice(0, limit);
};

export interface LabelSegment {
	match: boolean;
	text: string;
}

const highlightSegments = (label: string, query: string): LabelSegment[] => {
	const words = queryWords(query);
	if (words.length === 0) return [{ match: false, text: label }];
	const lowerLabel = Array.from(label, (character) => {
		const folded = normalizeForMatch(character);
		return folded.length === character.length ? folded : character.toLowerCase();
	}).join("");
	const matched = new Array<boolean>(label.length).fill(false);
	for (const word of words) {
		let index = lowerLabel.indexOf(word);
		while (index !== -1) {
			for (let position = index; position < index + word.length; position++) matched[position] = true;
			index = lowerLabel.indexOf(word, index + word.length);
		}
	}
	const segments: LabelSegment[] = [];
	for (let position = 0; position < label.length; position++) {
		const isMatch = matched[position];
		const previous = segments[segments.length - 1];
		if (previous && previous.match === isMatch) previous.text += label[position];
		else segments.push({ match: isMatch, text: label[position] });
	}
	return segments;
};

export { highlightSegments, normalize, queryWords, rankByLabel };
export default rankByLabel;
